import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { toast } from 'sonner';
import { partnerApi } from '@client/api/waterlily';
import { selectClient, selectSessionAdvisor } from '@client/model/selectors';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';

interface UseInvitePartnerOptions {
  onSuccess?: () => void;
}

export function useInvitePartner({ onSuccess }: UseInvitePartnerOptions = {}) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const advisor = useSelector(selectSessionAdvisor);

  const mutation = useMutation({
    mutationFn: (partnerEmail: string) => {
      return partnerApi.invitePartner({
        partnerEmail,
        advisorId: advisor?.advisorId ?? '',
        clientId: client.clientId,
        clientEmail: client.clientEmail ?? '',
        clientFirstName: client.clientFirstName ?? '',
        clientLastName: client.clientLastName ?? '',
      });
    },
    onSuccess: () => {
      dispatch(fetchClientByClientIdRequest(client.clientId));
      toast.success('Partner invitation sent successfully');
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('not found') || message.includes('Not found')) {
        toast.error('The account with the provided email address was not found for this advisor.');
      } else if (message.includes('multiple records') || message.includes('Multiple records')) {
        toast.error(
          'There are multiple clients with that email address for this advisor. Please contact your advisor to link the accounts for you.',
        );
      } else if (message.includes('already linked') || message.includes('Already linked')) {
        toast.error(
          'The account with the provided email address is already linked to another client.',
        );
      } else if (message.includes('no inference set') || message.includes('No inference set')) {
        toast.error(
          'The account with the provided email address has not submitted an intake form.',
        );
      } else {
        toast.error('Failed to send partner invitation. Please try again.');
      }
    },
  });

  return {
    invitePartner: mutation.mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    reset: mutation.reset,
  };
}
